/**
 * Mutation hooks for the Enterprise SSO admin surface.
 *
 * Three of the four edge-function actions (partner.create, partner.rotate_secret,
 * partner.deprovision_identity, partner.update_status) route through the
 * `enterprise-sso-admin` edge function so the secret-encryption + auth.admin
 * signOut work happens server-side with service-role privileges.
 *
 * The two admin-management mutations (add/remove partner admin) call the
 * SECURITY DEFINER RPCs directly via supabase.rpc — no encryption involved,
 * and the RPCs do their own caller_can_manage_partner gating.
 *
 * All hooks invalidate the appropriate query keys on success and capture
 * errors to GlitchTip per the project's catch-and-toast telemetry pattern.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';

export function useCreatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      name: string;
      slug: string;
      provisioning_mode: 'shared' | 'per_user';
      default_member_role?: string;
      portal_url?: string;
      initial_admin_user_ids: string[];
      workspace_owner_user_id: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('enterprise-sso-admin', {
        body: { action: 'partner.create', ...args },
      });
      if (error) throw error;
      return data as { partner_id: string; signing_secret_plaintext: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.list() }),
    onError: (err) => captureError(err as Error, { feature: 'enterprise-sso-create-partner' }),
  });
}

export function useRotatePartnerSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (partnerId: string) => {
      const { data, error } = await supabase.functions.invoke('enterprise-sso-admin', {
        body: { action: 'partner.rotate_secret', partner_id: partnerId },
      });
      if (error) throw error;
      return data as { signing_secret_plaintext: string };
    },
    onSuccess: (_d, partnerId) =>
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.detail(partnerId) }),
    onError: (err) => captureError(err as Error, { feature: 'enterprise-sso-rotate-secret' }),
  });
}

export function useDeprovisionIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { partner_id: string; external_user_id: string }) => {
      const { error } = await supabase.functions.invoke('enterprise-sso-admin', {
        body: { action: 'partner.deprovision_identity', ...args },
      });
      if (error) throw error;
    },
    onSuccess: (_d, args) =>
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.identities(args.partner_id) }),
    onError: (err) => captureError(err as Error, { feature: 'enterprise-sso-deprovision' }),
  });
}

export function useUpdatePartnerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { partner_id: string; status: 'active' | 'suspended' | 'deleted' }) => {
      const { error } = await supabase.functions.invoke('enterprise-sso-admin', {
        body: { action: 'partner.update_status', ...args },
      });
      if (error) throw error;
    },
    onSuccess: (_d, args) =>
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.detail(args.partner_id) }),
    onError: (err) => captureError(err as Error, { feature: 'enterprise-sso-update-status' }),
  });
}

export function useAddPartnerAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      partner_id: string;
      user_id: string;
      is_workspace_owner: boolean;
    }) => {
      const { error } = await supabase.rpc('enterprise_sso_partner_add_admin', {
        p_partner_id: args.partner_id,
        p_user_id: args.user_id,
        p_is_workspace_owner: args.is_workspace_owner,
      });
      if (error) throw error;
    },
    onSuccess: (_d, args) => {
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.admins(args.partner_id) });
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.detail(args.partner_id) });
    },
    onError: (err) => captureError(err as Error, { feature: 'enterprise-sso-add-admin' }),
  });
}

export function useRemovePartnerAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { partner_id: string; user_id: string }) => {
      const { error } = await supabase.rpc('enterprise_sso_partner_remove_admin', {
        p_partner_id: args.partner_id,
        p_user_id: args.user_id,
      });
      if (error) throw error;
    },
    onSuccess: (_d, args) =>
      qc.invalidateQueries({ queryKey: queryKeys.enterprisePartners.admins(args.partner_id) }),
    onError: (err) => captureError(err as Error, { feature: 'enterprise-sso-remove-admin' }),
  });
}
